/**
 * Category classification for notification preference gating — a fixed
 * type->category table, kept byte-for-byte identical to CATEGORY_BY_TYPE in
 * the backend's services/pushNotification.service.js (the authoritative
 * copy) and to botriq-user-mobile-app/src/store/notificationPrefsStore.js's
 * own copy. A substring heuristic ("startsWith('robot')", etc.) used to stand
 * in for this and quietly drifted from the backend's fixed lookup — e.g.
 * `destination_reached`/`emergency_stop`/`low_battery`/`startup_ready` matched
 * no substring rule here and were always shown, while the backend counted
 * them as muted the moment "Delivery updates"/"Robot status" was off. That
 * mismatch is exactly what made the header badge disagree with the rows this
 * page actually displays. Adding a new NOTIFICATION_TYPES entry means adding
 * one line here and mirroring it in the other two copies, not guessing
 * whether a heuristic still classifies it the same way.
 *
 * Deliberately NOT the same axis as Notifications.jsx's own TYPE_META `group`
 * (deliveries/system) — that one drives the page's filter tabs; this one
 * drives whether a row is shown at all, per the user's Settings preferences.
 */
const CATEGORY_BY_TYPE = {
  robot_connected: "robotStatus",
  robot_disconnected: "robotStatus",
  robot_assigned: "robotStatus",
  robot_removed: "robotStatus",
  sensor_health: "robotStatus",
  destination_reached: "deliveryUpdates",
  navigation_failed: "deliveryUpdates",
  navigation_cancelled: "deliveryUpdates",
  navigation_resumed: "deliveryUpdates",
  emergency_stop: "deliveryUpdates",
  low_battery: "robotStatus",
  startup_ready: "robotStatus",
  advertisement_approved: "advertisements",
  advertisement_rejected: "advertisements",
  advertisement_expired: "advertisements",
  registration_approved: "accountAlerts",
  registration_rejected: "accountAlerts",
};

export function prefKeyForType(type) {
  return CATEGORY_BY_TYPE[type] ?? null;
}

// A notification is visible unless the master switch or its category is
// explicitly toggled off. An uncategorized type is always shown once the
// master switch is on.
export function isNotificationEnabled(prefs, type) {
  if (prefs?.pushEnabled === false) return false;
  const key = prefKeyForType(type);
  if (!key) return true;
  return prefs?.[key] !== false;
}
